import { Controller } from "@hotwired/stimulus"
import mapboxSdk from '@mapbox/mapbox-sdk'
import mapboxgl from 'mapbox-gl'
import GeocodingClient from '@mapbox/mapbox-sdk/services/geocoding'

// Status color mapping for Access View markers
const STATUS_COLORS = {
  green: "#16a34a",
  yellow: "#eab308",
  red: "#dc2626",
  purple: "#7c3aed",
  none: "#6b7280"  // support-dark-500 grey for no data
}

// Connects to data-controller="mapbox"
export default class extends Controller {
  static values = {
    accessToken: String,
    address: String,
    accessView: { type: Boolean, default: false },
    geojson: { type: String, default: "" },
    latitude: { type: Number, default: 0 },
    longitude: { type: Number, default: 0 }
  }
  static targets = ["map", "mapError", "marker", "legend"]

  connect() {
    mapboxgl.accessToken = this.accessTokenValue
    try {
      this.map = new mapboxgl.Map({
        container: this.mapTarget.id,
        center: [-83.7487, 42.2658],
        zoom: 9, // starting zoom
        attributionControl: false
      });
      // Report the success so we have a baseline for how often it does work.
      fetch("/api/v1/metrics/increment_counter?name=mapbox.load.success", { method: "POST" });
    } catch (error) {
      // Report the error so we know how often it happens.
      fetch("/api/v1/metrics/increment_counter?name=mapbox.load.error", { method: "POST" });

      // Show an error message.
      this.mapErrorTarget.classList.remove('hidden');
      return;
    }

    if (this.accessViewValue) {
      this.map.on("load", () => this.initAccessViewMap())
    } else {
      this.initStandardMap()
    }
  }

  // Standard map initialization (existing behavior)
  initStandardMap() {
    if (this.addressValue) {
      const geocodingClient = new GeocodingClient({ accessToken: this.accessTokenValue })
      geocodingClient.forwardGeocode({
        query: this.addressValue,
        limit: 1
      })
      .send()
      .then(response => {
        const match = response.body;
        const [lng, lat] = match.features[0].center;
        this.map.flyTo({ center: [lng, lat], zoom: 9 });
      })
    }

    if (this.markerTargets.length > 0) {
      for (const markerTarget of this.markerTargets) {
        const lng = markerTarget.dataset.mapMarkerLongitude
        const lat = markerTarget.dataset.mapMarkerLatitude

        if (lng && lat) {
          new mapboxgl.Marker()
            .setLngLat([lng, lat])
            .addTo(this.map)
        }
      }
    }
  }

  // Access View map initialization — renders immediately, loads data lazily
  initAccessViewMap() {
    // If geojson is already available (non-lazy path), render immediately
    if (this.geojsonValue) {
      let geojson
      try {
        geojson = JSON.parse(this.geojsonValue)
      } catch (e) {
        geojson = null
      }

      if (geojson && geojson.features && geojson.features.length > 0) {
        if (this.latitudeValue && this.longitudeValue) {
          this.map.setCenter([this.longitudeValue, this.latitudeValue])
          this.map.setZoom(10)
        }
        this.renderAccessViewPins(geojson)
      }
    }

    // Listen for lazy-loaded data from Turbo Frame
    document.addEventListener("access-view:data-loaded", (e) => {
      const { geojson, latitude, longitude } = e.detail
      if (!geojson || !geojson.features || geojson.features.length === 0) return

      if (latitude && longitude) {
        this.map.setCenter([longitude, latitude])
        this.map.setZoom(10)
      }

      this.renderAccessViewPins(geojson)
    })
  }

  // Render provider pins on the map from GeoJSON data
  renderAccessViewPins(geojson) {
    // Remove existing source/layers if re-rendering
    if (this.map.getLayer("unclustered-point")) this.map.removeLayer("unclustered-point")
    if (this.map.getLayer("highlight-point")) this.map.removeLayer("highlight-point")
    if (this.map.getSource("access-providers")) this.map.removeSource("access-providers")

    // Add GeoJSON source without clustering - show all individual pins
    this.map.addSource("access-providers", {
      type: "geojson",
      data: geojson
    })

    // Individual provider markers
    // Color priority: green+networked=green, other status+networked=purple,
    // then status color, hide providers with no data and not networked
    this.map.addLayer({
      id: "unclustered-point",
      type: "circle",
      source: "access-providers",
      filter: ["any",
        ["!=", ["get", "status"], "none"],
        ["get", "networked"]
      ],
      paint: {
        "circle-color": [
          "case",
          // Green status always shows green (even if also networked)
          ["==", ["get", "status"], "green"], STATUS_COLORS.green,
          // Non-green + networked shows purple
          ["get", "networked"], STATUS_COLORS.purple,
          // Otherwise color by status
          ["match", ["get", "status"],
            "yellow", STATUS_COLORS.yellow,
            "red", STATUS_COLORS.red,
            STATUS_COLORS.none
          ]
        ],
        "circle-radius": 7,
        "circle-stroke-width": 1,
        "circle-stroke-color": "#ffffff"
      }
    })

    // Click on point to select provider
    this.map.on("click", "unclustered-point", (e) => {
      const npi = e.features[0].properties.id
      if (npi) {
        this.dispatch("mapProviderClick", { detail: { npi: String(npi) } })
      }
    })

    // Change cursor on hover
    this.map.on("mouseenter", "unclustered-point", () => {
      this.map.getCanvas().style.cursor = "pointer"
    })
    this.map.on("mouseleave", "unclustered-point", () => {
      this.map.getCanvas().style.cursor = ""
    })

    // Listen for provider selection events from the navigation controller
    this.element.addEventListener("partners-navigation:providerSelected", (e) => {
      this.highlightProvider(e.detail.npi)
    })
    this.element.addEventListener("partners-navigation:providerDeselected", () => {
      this.clearHighlight()
    })
  }

  // Highlight a selected provider on the map
  highlightProvider(npi) {
    if (!this.map || !this.map.getSource("access-providers")) return

    // Add or update highlight layer
    if (this.map.getLayer("highlight-point")) {
      this.map.setFilter("highlight-point", ["==", ["get", "id"], npi])
    } else {
      this.map.addLayer({
        id: "highlight-point",
        type: "circle",
        source: "access-providers",
        filter: ["==", ["get", "id"], npi],
        paint: {
          "circle-radius": 14,
          "circle-color": "transparent",
          "circle-stroke-width": 3,
          "circle-stroke-color": "#6366f1"
        }
      })
    }

    // Pan to the provider
    const source = this.map.getSource("access-providers")
    if (source && source._data) {
      const feature = source._data.features?.find(f => String(f.properties.id) === String(npi))
      if (feature) {
        this.map.easeTo({
          center: feature.geometry.coordinates,
          zoom: Math.max(this.map.getZoom(), 12)
        })
      }
    }
  }

  // Clear provider highlight
  clearHighlight() {
    if (!this.map) return
    if (this.map.getLayer("highlight-point")) {
      this.map.setFilter("highlight-point", ["==", ["get", "id"], ""])
    }
  }
}
